import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col, Form, Container, Button } from "react-bootstrap";
import { connect } from "react-redux";
import { isEmpty, sumBy } from "lodash";
import { GlobalSpinner, showSuccessAlert, showErrorAlert, roundToTwoDecimals } from "../../../../../global/globalFunctions";
import { setHeading } from "../../../accounts/account.actions";
import { updateOrder } from "./OrderBookingAction";
import { GetAreaApi } from "../AddArea/areaAction";
import { GetShopByAreaApi } from "../AddShop/Shop.action";
import { getProductData } from "../AddProduct/Product.action";
import Select from "react-select";
import moment from "moment";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";


const EditOrderBooking = (props) => {
  const [state, setState] = useState({
    orderDate: moment().format("YYYY-MM-DD"),
    areaId: "",
    areaName: "",
    shopId: "",
    shopName: "",
    discount: 0,
    taxAmount: 0,
    inputField: [],
    isSubmitting: false,
    isLoading: true,
  });

  useEffect(() => {
    props.setHeading("Edit Order Booking");
    if (!props.areaDataList.length) props.GetAreaApi();
    if (!props.productDataList.length) props.getProductData();
  }, []);

  useEffect(() => {
    if (!isEmpty(props.orderDetailById) && props.productDataList.length) {
      const order = props.orderDetailById[0];

      const updatedItems = order.items.map((item) => {
        const product = props.productDataList.find((p) => p._id === item.product) || {};
        const qty = Number(item.quantity);
        const price = roundToTwoDecimals(item.salePrice);
        return {
          code: item.code,
          product: item.product,
          productLabel: item.productLabel,
          quantity: qty,
          purchasePrice: roundToTwoDecimals(item.purchasePrice),
          salePrice: price,
          wholesalePrice: roundToTwoDecimals(item.wholesalePrice),
          availableQuantity: product.productBalance || 0,
          productTotalAmount: roundToTwoDecimals(qty * price),
        };
      });

      setState((prev) => ({
        ...prev,
        orderDate: order.orderDate || prev.date,
        areaId: order.areaId?._id || "",
        areaName: order.areaId?.areaName || "",
        shopId: order.shopId?._id || "",
        shopName: order.shopId?.shopName || "",
        discount: roundToTwoDecimals(order.discount),
        taxAmount: roundToTwoDecimals(order.taxAmount || 0),
        inputField: updatedItems,
        isLoading: false,
      }));

      if (order.areaId?._id) props.GetShopByAreaApi(order.areaId._id);
    }
  }, [props.orderDetailById, props.productDataList]);


  // ✅ Derived totals — always 2 decimal places
  const subTotal = useMemo(
    () => roundToTwoDecimals(sumBy(state.inputField, "productTotalAmount")),
    [state.inputField]
  );

  const taxableAmount = useMemo(
    () => roundToTwoDecimals(subTotal - state.discount),
    [subTotal, state.discount]
  );

  const grandTotal = useMemo(
    () => roundToTwoDecimals(taxableAmount + state.taxAmount),
    [taxableAmount, state.taxAmount]
  );

  const handleChangeArea = useCallback((selectedOption) => {
    setState((prev) => ({
      ...prev,
      areaId: selectedOption.value,
      areaName: selectedOption.label,
      shopId: "",
      shopName: "",
    }));
    props.GetShopByAreaApi(selectedOption.value);
  }, []);

  const handleChangeShop = useCallback((selectedOption) => {
    setState((prev) => ({
      ...prev,
      shopId: selectedOption.value,
      shopName: selectedOption.label,
    }));
  }, []);

  const handleProductChange = (selectedOption, index) => {
    const product = props.productDataList.find((p) => p._id === selectedOption.value);
    if (!product) return;

    const price = roundToTwoDecimals(product.salePrice);
    setState((prev) => {
      const list = [...prev.inputField];
      list[index] = {
        ...list[index],
        product: product._id,
        productLabel: product.name,
        code: product.code,
        availableQuantity: product.productBalance,
        quantity: 0,
        salePrice: price,
        purchasePrice: roundToTwoDecimals(product.purchasePrice),
        wholesalePrice: roundToTwoDecimals(product.wholesalePrice),
        productTotalAmount: price, // qty=1, so total = price
      };
      return { ...prev, inputField: list };
    });
  };

  const handleQuantityChange = (e, index) => {
    const value = Number(e.target.value);
    setState((prev) => {
      const list = [...prev.inputField];
      const item = list[index];
      const safeQty = Math.max(1, Math.min(value, item.availableQuantity));
      list[index] = {
        ...item,
        quantity: safeQty,
        productTotalAmount: roundToTwoDecimals(safeQty * item.salePrice),
      };
      return { ...prev, inputField: list };
    });
  };

  const handlePriceChange = (e, index) => {
    const value = roundToTwoDecimals(Math.max(0, Number(e.target.value)));
    setState((prev) => {
      const list = [...prev.inputField];
      const item = list[index];
      list[index] = {
        ...item,
        salePrice: value,
        productTotalAmount: roundToTwoDecimals(value * item.quantity),
      };
      return { ...prev, inputField: list };
    });
  };

  const handleDiscountChange = (e) => {
    const value = roundToTwoDecimals(Math.max(0, Math.min(Number(e.target.value), subTotal)));
    setState((prev) => ({ ...prev, discount: value }));
  };

  const handleTaxChange = (e) => {
    const value = roundToTwoDecimals(Math.max(0, Number(e.target.value)));
    setState((prev) => ({ ...prev, taxAmount: value }));
  };

  const handleAddProduct = () => {
    setState((prev) => ({
      ...prev,
      inputField: [
        ...prev.inputField,
        {
          code: "",
          product: "",
          productLabel: "",
          quantity: 0,
          salePrice: 0.00,
          purchasePrice: 0.00,
          wholesalePrice: 0.00,
          availableQuantity: 0,
          productTotalAmount: 0.00,
        },
      ],
    }));
  };

  const handleRemoveProduct = (index) => {
    setState((prev) => ({
      ...prev,
      inputField: prev.inputField.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    if (state.inputField.some((item) => !item.product)) {
      return showErrorAlert("Please select all products");
    }

    setState((prev) => ({ ...prev, isSubmitting: true }));

    const orderData = {
      _id: props.orderDetailById[0]._id,
      areaId: state.areaId,
      shopId: state.shopId,
      subTotal,
      discount: state.discount,
      taxAmount: state.taxAmount,
      grandTotal,
      orderDate: state.orderDate,
      items: state.inputField.map((item) => ({
        code: item.code,
        product: item.product,
        productLabel: item.productLabel,
        quantity: item.quantity,
        purchasePrice: item.purchasePrice,
        salePrice: item.salePrice,
        wholesalePrice: item.wholesalePrice,
      })),
    };

    props
      .updateOrder(orderData, showSuccessAlert, showErrorAlert)
      .then(() => {
        setState((prev) => ({ ...prev, isSubmitting: false }));
        window.history.back();
      })
      .catch(() => {
        setState((prev) => ({ ...prev, isSubmitting: false }));
      });
  };

  const AreaListData = useMemo(
    () => props.areaDataList.map((a) => ({ value: a._id, label: a.areaName })),
    [props.areaDataList]
  );
  const ShopListData = useMemo(
    () => props.shopsByAreaList.map((s) => ({ value: s._id, label: s.shopName })),
    [props.shopsByAreaList]
  );
  const ProductOptions = useMemo(
    () => props.productDataList.map((p) => ({ value: p._id, label: p.name })),
    [props.productDataList]
  );

  if (state.isLoading || props.isFetchingProduct || props.isFetchingOrderDetailById) {
    return <GlobalSpinner />;
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label><b>Date</b></Form.Label>
            <Form.Control
              type="date"
              value={state.orderDate}
              onChange={(e) => setState((prev) => ({ ...prev, orderDate: e.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label><b>Select Area *</b></Form.Label>
            <Select
              value={{ value: state.areaId, label: state.areaName }}
              options={AreaListData}
              onChange={handleChangeArea}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3">
            <Form.Label><b>Select Shop *</b></Form.Label>
            <Select
              value={{ value: state.shopId, label: state.shopName }}
              options={ShopListData}
              onChange={handleChangeShop}
              isDisabled={!state.areaId}
            />
          </Form.Group>
        </Col>
      </Row>

      {state.inputField.map((item, index) => (
        <React.Fragment key={index}>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label><b>Product *</b></Form.Label>
                <Select
                  value={{ value: item.product, label: item.productLabel }}
                  options={ProductOptions}
                  onChange={(e) => handleProductChange(e, index)}
                  isOptionDisabled={(option) =>
                    state.inputField.some((i, idx) => idx !== index && i.product === option.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label><b>Available</b></Form.Label>
                <Form.Control
                  type="number"
                  disabled
                  value={item.availableQuantity}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label><b>Quantity *</b></Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={item.availableQuantity}
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(e, index)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label><b>Price *</b></Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={item.salePrice.toFixed(2)}
                  onChange={(e) => handlePriceChange(e, index)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label><b>Total</b></Form.Label>
                <Form.Control
                  type="number"
                  disabled
                  value={item.productTotalAmount.toFixed(2)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              {state.inputField.length > 1 && (
                <Button
                  variant="danger"
                  onClick={() => handleRemoveProduct(index)}
                  className="float-end me-2"
                >
                  <RemoveIcon /> Remove
                </Button>
              )}
              {index === state.inputField.length - 1 && (
                <Button variant="primary" onClick={handleAddProduct} className="float-end">
                  <AddIcon /> Add Product
                </Button>
              )}
            </Col>
          </Row>
        </React.Fragment>
      ))}

      <Row>
        <Col>
          <Form.Group className="mb-3" controlId="garana">
            <Form.Label><b>Sub Total</b></Form.Label>
            <Form.Control
              type="number"
              disabled
              value={subTotal}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3" controlId="garana">
            <Form.Label><b>Discount</b></Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={subTotal}
              value={state.discount}
              onChange={handleDiscountChange}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3" controlId="garana">
            <Form.Label><b>Tax</b></Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={state.taxAmount}
              onChange={handleTaxChange}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3" controlId="garana">
            <Form.Label><b>Grand Total</b></Form.Label>
            <Form.Control
              type="number"
              disabled
              value={grandTotal}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col className="text-end">
          <Button variant="secondary" onClick={() => window.history.back()} className="me-2">Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={state.isSubmitting || !state.areaId || !state.shopId || state.inputField.some((item) => !item.product || item.quantity <= 0)}
          >
            {state.isSubmitting ? "Updating..." : "Update Order"}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default connect(
  (state) => ({
    areaDataList: state.AreaReducer.areaDataList,
    shopsByAreaList: state.ShopReducer.shopsByAreaList,
    productDataList: state.ProductReducer.productDataList,
    orderDetailById: state.OrderBookingReducer.orderDetailById,
    isFetchingProduct: state.ProductReducer.isFetchingProduct,
    isFetchingOrderDetailById: state.OrderBookingReducer.isFetchingOrderDetailById,
  }),
  { GetAreaApi, GetShopByAreaApi, updateOrder, setHeading, getProductData }
)(EditOrderBooking);